import { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, logout as apiLogout, refreshToken } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export const ROLES = {
  CANDIDATE: 'CANDIDATE',
  COMPANY: 'COMPANY',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction réutilisable pour enregistrer l'utilisateur
  const handleAuthSuccess = (userData) => {
    const formattedUser = {
      id: userData.id,
      email: userData.email,
      role: userData.role,
    };
    setUser(formattedUser);
    localStorage.setItem('user', JSON.stringify(formattedUser));
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        // 1. 💡 AJOUT : Est-ce qu'on revient de Google avec un token/status dans l'URL ?
        const params = new URLSearchParams(window.location.search);
        const googleStatus = params.get('status'); 
        // Note: Ajuste 'status' ou 'token' selon ce que ton backend injecte dans l'URL au retour
        
        if (googleStatus === 'success') {
          // Si le backend a mis les cookies et nous redirige avec ?status=success
          // On nettoie l'URL proprement pour enlever le ?status=success
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // 2. Appel normal au backend pour récupérer le profil (Via cookies HttpOnly)
        const response = await api.get('/auth/me/');
        
        if (response.data.status && response.data.user) {
          handleAuthSuccess(response.data.user);
        }
      } catch (err) {
        // Pas authentifié ou token expiré
        setUser(null);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await apiLogin(email, password);
      
      if (!response.status || !response.user) {
        throw new Error('Login failed');
      }
      
      handleAuthSuccess(response.user);
      return { success: true, role: response.user.role };
    } catch (err) {
      const errorMsg = err.response?.data?.details || 'Authentication failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const setUserRole = (role) => {
    const userData = { ...user, role };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshAccessToken = async () => {
    try {
      await refreshToken();
      return true;
    } catch (err) {
      await logout();
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        setUserRole,
        refreshAccessToken,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};